/**
 * CRM page — "Track a service" card (DNKADM-0499). Up to {@link CRM_TRACK_MAX} services;
 * each one adds "Dink Last <service>" to every contact. The card is read-only (the 0493
 * {@link SummaryCard}); its one Edit opens an {@link EditDrawer} with a checkbox per service.
 * Once {@link CRM_TRACK_MAX} are ticked the rest are disabled with "Up to 3 services".
 *
 * Save writes each changed service through the ordinary service update (`crmTracked` on
 * `PUT /v2/admin/services/{id}`, the body built from the freshly read record so nothing
 * else changes). Turn-offs go first, so swapping one service for another at the limit
 * works. The first failure stops the save and keeps the drawer open on a plain sentence;
 * a `409 CRM_TRACK_LIMIT` reads the fixed limit line, never the code.
 */
import { useCallback, useState } from 'react';
import {
  Alert,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Skeleton,
  Typography,
} from '@mui/material';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import { getService, listServices, serviceInputFrom, updateService } from '@/api/services';
import { CRM_TRACK_MAX, crmErrorMessage, type CrmTrackedService } from '@/api/crm';
import { SummaryCard, SummaryRow } from '@/features/services/SummaryCard';
import { EditDrawer } from '@/features/services/EditDrawer';

interface ServiceChoice {
  id: string;
  name: string;
}

export function TrackServicesCard({
  tracked,
  onChanged,
}: {
  tracked: readonly CrmTrackedService[];
  /** Re-read the page after a save (tracked services and their fields). */
  onChanged: () => void;
}) {
  const showToast = useToast();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const services = useResource(
    useCallback(
      () => (open ? listServices() : Promise.resolve(null)),
      [open],
    ),
    [open],
  );

  // Every service the drawer offers: the live list, plus any tracked service it lacks (so a
  // tracked service that has since been turned off can still be untracked).
  const choices: ServiceChoice[] = [
    ...(services.data ?? []).map((s) => ({ id: s.id, name: s.name || s.id })),
  ];
  for (const t of tracked) {
    if (!choices.some((c) => c.id === t.serviceId)) choices.push({ id: t.serviceId, name: t.name });
  }

  const atLimit = selected.size >= CRM_TRACK_MAX;

  function openDrawer() {
    setSelected(new Set(tracked.map((t) => t.serviceId)));
    setError(null);
    setOpen(true);
  }

  function toggle(id: string, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function save(): Promise<boolean> {
    setError(null);
    const was = new Set(tracked.map((t) => t.serviceId));
    const turnOff = [...was].filter((id) => !selected.has(id));
    const turnOn = [...selected].filter((id) => !was.has(id));
    const changes: Array<[string, boolean]> = [
      ...turnOff.map((id): [string, boolean] => [id, false]),
      ...turnOn.map((id): [string, boolean] => [id, true]),
    ];
    for (const [id, crmTracked] of changes) {
      try {
        const service = await getService(id);
        if (service === null) throw new Error('missing service');
        await updateService(id, { ...serviceInputFrom(service), crmTracked });
      } catch (err) {
        setError(crmErrorMessage(err, 'The tracked services couldn’t be saved. Please try again.'));
        onChanged();
        return false;
      }
    }
    if (changes.length > 0) {
      showToast('Tracked services saved', 'success');
      onChanged();
    }
    return true;
  }

  return (
    <>
      <SummaryCard
        title="Track a service"
        onEdit={openDrawer}
        empty={tracked.length === 0 ? 'No services tracked yet' : undefined}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
          Each tracked service adds a “Dink Last &lt;service&gt;” date to every contact. Up to{' '}
          {CRM_TRACK_MAX} services.
        </Typography>
        {tracked.map((t, i) => (
          <SummaryRow key={t.serviceId} label={t.name} value={t.fieldName} last={i === tracked.length - 1} />
        ))}
      </SummaryCard>

      <EditDrawer
        open={open}
        title="Track services"
        onClose={() => setOpen(false)}
        onSave={save}
        saveDisabled={services.status !== 'ready'}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Picking a service adds “Dink Last &lt;service&gt;” to every contact: the date of their
          most recent visit for it.
        </Typography>
        {services.status === 'loading' && (
          <Skeleton variant="rounded" height={120} role="status" aria-label="Loading services" />
        )}
        {services.status === 'error' && (
          <Alert severity="error">The services couldn’t be loaded. Close and try again.</Alert>
        )}
        {services.status === 'ready' && (
          <>
            <FormGroup>
              {choices.map((c) => {
                const checked = selected.has(c.id);
                return (
                  <FormControlLabel
                    key={c.id}
                    control={
                      <Checkbox
                        checked={checked}
                        disabled={!checked && atLimit}
                        onChange={(e) => toggle(c.id, e.target.checked)}
                      />
                    }
                    label={c.name}
                  />
                );
              })}
            </FormGroup>
            {choices.length === 0 && (
              <Typography variant="body2">No services yet. Add one under Services first.</Typography>
            )}
            {atLimit && <FormHelperText>Up to {CRM_TRACK_MAX} services</FormHelperText>}
          </>
        )}
        {error && (
          <Alert severity="error" role="alert" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </EditDrawer>
    </>
  );
}
